import { Avatar, Box, Button, Stack, Typography } from '@mui/material'
import { useMemo } from 'react'
import { EmphasizedNumber, Fade, ItemImageArea } from 'utoniq-ui'

import {
  ItemThumbnailType,
  Maybe,
  MissionRewardConnectionFragment,
} from '@/clients/utoniq-core/schema'
import { BaseLayout, Head } from '@/components/layouts'
import { ArtistThemeProvider } from '@/components/styles'
import { useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'

export * from './fragments'

export type State = {
  isFromArtistMission: boolean
  mission: Maybe<MissionRewardConnectionFragment>
}

type HeadingTextCellProps = {
  mission: MissionRewardConnectionFragment
}

type ItemImageAreaCellProps = {
  mission: MissionRewardConnectionFragment
}

type ButtonsCellProps = {
  isFromArtistMission: boolean
  mission: MissionRewardConnectionFragment
}

export type Action = {}

export const MissionAccomplishedTemplate: Template<State, Action> = ({
  state,
}) => {
  const { routerNames } = useRouter()
  if (!state.mission) {
    return null
  }
  return (
    <ArtistThemeProvider artistId={state.mission.artistId}>
      <BaseLayout>
        <Head title={routerNames.MissionAccomplished} />
        <HeadingTextCell mission={state.mission} />
        <ItemImageAreaCell mission={state.mission} />
        <ButtonsCell
          mission={state.mission}
          isFromArtistMission={state.isFromArtistMission}
        />
      </BaseLayout>
    </ArtistThemeProvider>
  )
}

const HeadingTextCell: Cell<HeadingTextCellProps> = ({ mission }) => {
  const { t } = useTranslation('missionAccomplished')
  return (
    <CellFrame sx={{ py: 5 }}>
      <Stack spacing={2} direction="column" alignItems="center">
        <Typography variant="h3">{t('completedMessage')}</Typography>
        <Typography variant="body1" sx={{ pt: 2 }}>
          {mission.reward.gachaTicket ? t('getGachaTicket') : t('getItem')}
        </Typography>
      </Stack>
    </CellFrame>
  )
}

const ItemImageAreaCell: Cell<ItemImageAreaCellProps> = ({ mission }) => {
  const { t } = useTranslation('missionAccomplished')
  return (
    <CellFrame sx={{ py: 3 }}>
      <Stack alignItems="center">
        {mission.reward.gachaTicket ? (
          <>
            <Fade>
              <Avatar
                variant="square"
                sx={{
                  width: 108,
                  height: 108,
                  bgcolor: 'grey.200',
                  borderRadius: 2,
                }}
                src={
                  mission.reward.gachaTicket.gachaTicket.thumbnails?.reward
                    ?.smallUri ?? ''
                }
              >
                {!mission.reward.gachaTicket.gachaTicket.thumbnails?.reward
                  ?.smallUri && (
                  <Typography variant="body2" color="text.hint">
                    {t('gachaTicketImage')}
                  </Typography>
                )}
              </Avatar>
            </Fade>
            <Fade
              sx={{
                zIndex: 1,
              }}
            >
              <Box
                sx={{
                  bgcolor: 'grey.100',
                  width: 80,
                  height: 20,
                  borderRadius: 3,
                  textAlign: 'center',
                  mt: -2,
                  zIndex: 1,
                }}
              >
                <EmphasizedNumber variant="body2">
                  x{mission.reward.gachaTicket.count}
                </EmphasizedNumber>
              </Box>
            </Fade>
          </>
        ) : (
          <Fade>
            <ItemImageArea
              size={
                mission.reward.item?.item?.thumbnailType ===
                  ItemThumbnailType.Square ||
                mission.reward.item?.item?.thumbnailType ===
                  ItemThumbnailType.SquareTemplate
                  ? 'large-square'
                  : 'large'
              }
              src={mission.reward.item?.item?.thumbnail?.front?.smallUri ?? ''}
            />
          </Fade>
        )}
      </Stack>
    </CellFrame>
  )
}

const ButtonsCell: Cell<ButtonsCellProps> = ({
  mission,
  isFromArtistMission,
}) => {
  const { t } = useTranslation('missionAccomplished')
  const { routerPages, generatePath } = useRouter()

  const goBackRef = useMemo(() => {
    if (mission.noAnswer) {
      return generatePath(
        !isFromArtistMission ? routerPages.GlobalHome : routerPages.ArtistTop,
        { artistId: mission.artistId }
      )
    }
    return generatePath(
      !isFromArtistMission
        ? routerPages.GlobalEvents
        : routerPages.ArtistMissions,
      {
        tab: 'mission',
        artistId: mission.artistId,
      }
    )
  }, [
    generatePath,
    isFromArtistMission,
    mission.artistId,
    mission.noAnswer,
    routerPages.ArtistMissions,
    routerPages.ArtistTop,
    routerPages.GlobalEvents,
    routerPages.GlobalHome,
  ])

  return (
    <CellFrame sx={{ py: 5 }}>
      <Stack spacing={4} direction="column" alignItems="center">
        {mission.reward.gachaTicket ? (
          <Button
            href={generatePath(routerPages.ArtistGachas, {
              artistId: mission.artistId,
            })}
            variant="contained"
            size="large"
            sx={{ width: 244 }}
          >
            {t('turnGacha')}
          </Button>
        ) : (
          <Button
            href={generatePath(routerPages.OwnedItemDetail, {
              itemId: mission.reward.item?.item?.id,
            })}
            variant="contained"
            size="large"
            sx={{ width: 244 }}
          >
            {t('checkItem')}
          </Button>
        )}
        <Button
          href={goBackRef}
          variant="outlined"
          size="large"
          sx={{ width: 244 }}
        >
          {t(mission.noAnswer ? 'retrunTop' : 'returnMissionList')}
        </Button>
      </Stack>
    </CellFrame>
  )
}
